import React from 'react';
import Header from './Header';
import './App.css';

function APIPage() {
  // Header wird jetzt in Header.js verwaltet

  const endpoints = [
    {
      method: 'GET',
      path: '/api',
      title: 'API Info',
      description: 'Get API information and available endpoints',
      example: 'curl https://devradar.fun/api',
      response: {
        success: true,
        name: 'DevRadar.fun API',
        version: '1.0.0',
        description: 'Real-time memecoin launch tracking and developer analytics',
        endpoints: { /* ... */ }
      }
    },
    {
      method: 'GET',
      path: '/api/health',
      title: 'Health Check',
      description: 'Check API health and server status',
      example: 'curl https://devradar.fun/api/health',
      response: {
        success: true,
        status: 'ok',
        timestamp: '2025-12-27T13:00:00.000Z',
        uptime: 3600.5,
        zmqConnected: true,
        trackedCoins: 500
      }
    },
    {
      method: 'GET',
      path: '/api/launches',
      title: 'Get All Launches',
      description: 'Get all dev launches with optional filtering and sorting',
      params: [
        { name: 'limit', type: 'number', default: '500', desc: 'Maximum number of results' },
        { name: 'minBondingRate', type: 'number', default: '5', desc: 'Minimum bonding rate in %' },
        { name: 'minMarketCap', type: 'number', default: '0', desc: 'Minimum market cap in USD' },
        { name: 'sortBy', type: 'string', default: 'age', desc: 'Sort by "age" or "marketcap"' },
        { name: 'sortOrder', type: 'string', default: 'asc', desc: 'Sort order "asc" or "desc"' }
      ],
      example: 'curl "https://devradar.fun/api/launches?limit=100&minBondingRate=10&sortBy=marketcap&sortOrder=desc"',
      response: {
        success: true,
        data: [/* launches */],
        count: 100,
        total: 500,
        filters: {
          minBondingRate: 10,
          minMarketCap: 10000,
          sortBy: 'marketcap',
          sortOrder: 'desc',
          limit: 100
        },
        timestamp: '2025-12-27T13:00:00.000Z'
      }
    },
    {
      method: 'GET',
      path: '/api/leaderboard',
      title: 'Get Leaderboard',
      description: 'Get developer leaderboard sorted by bonding rate',
      params: [
        { name: 'limit', type: 'number', default: '200', desc: 'Maximum number of results' },
        { name: 'minBondingRate', type: 'number', default: '0.05', desc: 'Minimum bonding rate' },
        { name: 'page', type: 'number', default: '1', desc: 'Page number' },
        { name: 'pageSize', type: 'number', default: '50', desc: 'Results per page' }
      ],
      example: 'curl "https://devradar.fun/api/leaderboard?limit=50&minBondingRate=0.1"',
      response: {
        success: true,
        data: [/* developers */],
        count: 50,
        timestamp: '2025-12-27T13:00:00.000Z'
      }
    },
    {
      method: 'GET',
      path: '/api/dev/:creator',
      title: 'Get Developer Details',
      description: 'Get detailed information about a specific developer and all their coins',
      params: [
        { name: 'creator', type: 'string', required: true, desc: 'Developer wallet address (URL encoded)' }
      ],
      example: 'curl "https://devradar.fun/api/dev/9QGCZ6n4paVDe2Pa9HEz6zpBc4aoGdpp8eyLwsWxX4mE"',
      response: {
        success: true,
        creator: '9QGCZ6n4paVDe2Pa9HEz6zpBc4aoGdpp8eyLwsWxX4mE',
        stats: {
          totallaunches: 2,
          bondedcount: 2,
          bonding_rate: 1.0,
          bonding_rate_percent: '100.00'
        },
        coins: [/* coins */],
        count: 2
      }
    },
    {
      method: 'GET',
      path: '/api/dex',
      title: 'Get DEX Status',
      description: 'Get all coins with DEX payment status (PENDING, APPROVED, or PAID)',
      example: 'curl https://devradar.fun/api/dex',
      response: {
        success: true,
        data: [/* coins with DEX status */],
        count: 6,
        timestamp: '2025-12-27T13:00:00.000Z'
      }
    }
  ];

  return (
    <div className="App">
      <Header />

      <main className="api-page">
        <div className="api-container">
          <div className="api-header">
            <h1>📡 DevRadar.fun API</h1>
            <p className="api-description">
              Real-time memecoin launch tracking and developer analytics API.
              All endpoints return JSON and are publicly accessible.
            </p>
            <div className="api-info">
              <div className="info-item">
                <span className="info-label">Base URL:</span>
                <code className="info-value">https://devradar.fun/api</code>
              </div>
              <div className="info-item">
                <span className="info-label">Version:</span>
                <span className="info-value">1.0.0</span>
              </div>
              <div className="info-item">
                <span className="info-label">Format:</span>
                <span className="info-value">JSON</span>
              </div>
            </div>
          </div>

          <div className="endpoints-list">
            {endpoints.map((endpoint, index) => (
              <div key={index} className="endpoint-card">
                <div className="endpoint-header">
                  <span className={`method-badge method-${endpoint.method.toLowerCase()}`}>
                    {endpoint.method}
                  </span>
                  <code className="endpoint-path">{endpoint.path}</code>
                  <h3 className="endpoint-title">{endpoint.title}</h3>
                </div>
                
                <p className="endpoint-description">{endpoint.description}</p>

                {endpoint.params && endpoint.params.length > 0 && (
                  <div className="endpoint-params">
                    <h4>Query Parameters:</h4>
                    <table className="params-table">
                      <thead>
                        <tr>
                          <th>Parameter</th>
                          <th>Type</th>
                          <th>Default</th>
                          <th>Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        {endpoint.params.map((param, pIndex) => (
                          <tr key={pIndex}>
                            <td>
                              <code>{param.name}</code>
                              {param.required && <span className="required">*</span>}
                            </td>
                            <td><code>{param.type}</code></td>
                            <td>{param.default || '-'}</td>
                            <td>{param.desc}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <div className="endpoint-example">
                  <h4>Example Request:</h4>
                  <pre className="code-block">
                    <code>{endpoint.example}</code>
                  </pre>
                </div>

                <div className="endpoint-response">
                  <h4>Example Response:</h4>
                  <pre className="code-block">
                    <code>{JSON.stringify(endpoint.response, null, 2)}</code>
                  </pre>
                </div>
              </div>
            ))}
          </div>

          <div className="api-footer">
            <h2>Response Format</h2>
            <p>All endpoints return JSON with the following structure:</p>
            <div className="response-format">
              <div className="format-item">
                <h4>Success Response:</h4>
                <pre className="code-block">
                  <code>{JSON.stringify({
                    success: true,
                    data: [/* ... */],
                    count: 100,
                    timestamp: '2025-12-27T13:00:00.000Z'
                  }, null, 2)}</code>
                </pre>
              </div>
              <div className="format-item">
                <h4>Error Response:</h4>
                <pre className="code-block">
                  <code>{JSON.stringify({
                    success: false,
                    error: 'Error message',
                    timestamp: '2025-12-27T13:00:00.000Z'
                  }, null, 2)}</code>
                </pre>
              </div>
            </div>

            <div className="api-notes">
              <h3>Notes:</h3>
              <ul>
                <li>All endpoints are publicly accessible (no authentication required)</li>
                <li>Rate limiting may be implemented in the future</li>
                <li>Most endpoints include <code>Cache-Control</code> headers (30-60 seconds)</li>
                <li>All timestamps are in ISO 8601 format (UTC)</li>
              </ul>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export default APIPage;

